import { useCallback, useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';

import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import LinkIcon from '@atlaskit/icon/core/link';
import { token } from '@atlaskit/tokens';

import { Button } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';

import { useSelector } from '../context/hooks';
import { selectShareableQuery } from '../context/selectors';
import { toQueryString } from '../util/url';

/**
 * Props for ShareButton — callers can override the visible label.
 * Kept as an optional prop so existing call sites (Estimate step) don't
 * need to change; only callers that want a variant (e.g. Summary step on
 * automations meter → "Share with team") pass an override.
 */
export type ShareButtonProps = {
	/**
	 * Visible label shown next to the link icon in the idle state.
	 * Defaults to "Copy URL to share" per Figma node 1103:32350. Callers
	 * override this (e.g. Summary step for the automations meter uses
	 * "Share with team" per the Confluence spec).
	 */
	label?: string;
	/** Optional callback fired when the user clicks the share button (before clipboard write). */
	onShare?: () => void;
};

/**
 * Copy-URL-to-share button for the Estimate / Summary steps.
 *
 * Uses the WAC design-library `Button` (primary, small) with the atlaskit
 * `link` icon before the label — matching Figma node 1103:32350 where the
 * share affordance sits alongside a Back button on the bottom action row of
 * the Estimate step.
 */
export const ShareButton = ({
	label = 'Copy link to share',
	onShare: onShareCallback,
}: ShareButtonProps = {}) => {
	const query = useSelector(selectShareableQuery);
	// Popup open state — anchored to the button, dismissed after a short
	// timeout so it behaves like a passive confirmation, not a modal.
	const [isCopiedOpen, setIsCopiedOpen] = useState(false);
	const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clean up the auto-dismiss timer if the component unmounts before it
	// fires — otherwise React logs a "state update on unmounted component"
	// warning in dev.
	useEffect(() => {
		return () => {
			if (dismissTimerRef.current) {
				clearTimeout(dismissTimerRef.current);
			}
		};
	}, []);

	const onShare = useCallback(async () => {
		onShareCallback?.();
		const url = buildShareUrl(toQueryString(query));
		try {
			await navigator.clipboard.writeText(url);
			setIsCopiedOpen(true);
			if (dismissTimerRef.current) {
				clearTimeout(dismissTimerRef.current);
			}
			dismissTimerRef.current = setTimeout(() => setIsCopiedOpen(false), 2000);
		} catch {
			// Clipboard may be unavailable (e.g. insecure context) — fall back.
			window.prompt('Copy this URL:', url);
		}
	}, [query, onShareCallback]);

	return (
		// ─────────────────────────────────────────────────────────────────
		// Why an inline absolutely-positioned bubble instead of
		// @atlaskit/popup or @atlaskit/tooltip
		// ─────────────────────────────────────────────────────────────────
		// Design intent (Figma spec): after the user clicks the button, a
		// small "Copied to clipboard" confirmation appears directly above
		// the button for ~2s. First choice was @atlaskit/popup — that's the
		// ADS-blessed component for exactly this pattern.
		//
		// Both @atlaskit/popup and @atlaskit/tooltip (also tested) render
		// their content via React portal + Popper.js. Popper resolves the
		// anchor position by walking the trigger element's ancestor chain,
		// looking for a positioned/scrolling container to anchor against.
		//
		// Inside the calculator shell, that ancestor scan misfires: the
		// bubble mounts in the DOM (aria-describedby wires up, event
		// handlers fire, screen readers announce it) but the computed
		// coordinates snap to `(0, viewportTop)` regardless of trigger
		// location, `strategy: "fixed"`, `shouldRenderToParent`, or offset.
		//
		// We hit the same class of bug with @atlaskit/tooltip on the "?"
		// icon in needs-step.tsx (see UsageHelpTooltip). Root cause is
		// something in the calculator shell's stacking / clipping / CSS-
		// containment context — a proper fix belongs at the shell level
		// (not in every consumer). Until that lands, the pragmatic
		// workaround is to render the confirmation inline as a normal
		// absolutely-positioned sibling of the trigger, escaping Popper's
		// ancestor-scan entirely.
		//
		// Trade-off: an inline `absolute` bubble is constrained by the
		// nearest `overflow: hidden` ancestor. That's acceptable here
		// because (a) the calculator shell doesn't clip the action-row
		// area, and (b) the bubble is small and directly above the button.
		// If we ever nest this button inside a clipped container, we'd
		// need to revisit — either the ancestor overflow, or return to a
		// portal-based approach with an explicit `getReferenceElement`
		// override on Popper.
		// ─────────────────────────────────────────────────────────────────
		<div css={sharePositionWrapperStyle}>
			<Button variant="primary" size="small" onClick={onShare}>
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: '8px',
					}}
				>
					<LinkIcon label="" />
					{label}
				</span>
			</Button>
			{isCopiedOpen ? (
				<div css={copiedBubbleStyle} role="status" aria-live="polite">
					<CheckCircleIcon label="" color={token('color.icon.success')} />
					<span>Copied to clipboard</span>
				</div>
			) : null}
		</div>
	);
};

// Wrapper — provides the position: relative anchor for the confirmation.
// Inline-flex keeps it sized to the button's width.
const sharePositionWrapperStyle = css({
	position: 'relative',
	display: 'inline-flex',
});

// Copy confirmation bubble — positioned directly above the button.
// Uses ADS elevation.surface.overlay + shadow to read as a floating hint.
const copiedBubbleStyle = css({
	position: 'absolute',
	insetBlockEnd: 'calc(100% + 8px)',
	insetInlineStart: '50%',
	transform: 'translateX(-50%)',
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100'),
	paddingBlock: token('space.100'),
	paddingInline: token('space.200'),
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: '4px',
	boxShadow: token('elevation.shadow.overlay'),
	fontSize: '14px',
	lineHeight: '20px',
	color: wacToken('color.neutral.light1100'),
	whiteSpace: 'nowrap',
	zIndex: 100,
});

const buildShareUrl = (queryString: string): string => {
	const { origin, pathname } = window.location;
	return `${origin}${pathname}${queryString ? `?${queryString}` : ''}`;
};
